import { useRef, useState } from "react";
import Layout from "../components/Layout";
import CountdownTimer from "../components/CountdownTimer";
import ShareButton from "../components/ShareButton";
import DownloadImageButton from "../components/DownloadImageButton";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Play, RefreshCw, RotateCcw } from "lucide-react";

const COLOR = "#06B6D4";
const DURATIONS = [30, 60, 90];

const PenBattle = () => {
  const [duration, setDuration] = useState(60);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [phase, setPhase] = useState("idle"); // idle | ready | running | done
  const cardRef = useRef(null);

  const fetchPrompt = async (d = duration) => {
    setLoading(true);
    try {
      const res = await api.post("/generate/pen-battle", { duration: d });
      setData(res.data);
      setPhase("ready");
    } catch (e) {
      toast.error("No se pudo generar el reto.");
    } finally {
      setLoading(false);
    }
  };

  const start = () => setPhase("running");
  const finish = () => setPhase("done");
  const reset = () => { setData(null); setPhase("idle"); };

  const reto = data?.data?.reto;
  const tip = data?.data?.tip;

  return (
    <Layout accent={COLOR}>
      <section className="max-w-5xl mx-auto px-5 sm:px-8 pt-10 sm:pt-16">
        <div className="mb-8">
          <span className="inline-block font-mono text-xs uppercase tracking-[0.25em] font-bold bg-[#06B6D4] text-white border-4 border-[#1a1a1a] rounded-full px-4 py-1 mb-4">
            tool · 02
          </span>
          <h1 className="font-display font-black text-5xl sm:text-7xl leading-[0.95] tracking-tight mb-4">
            pen <span className="text-[#06B6D4]">battle</span>.
          </h1>
          <p className="font-mono text-sm sm:text-base text-[#4a4a4a] max-w-xl">
            La IA te lanza un reto. Tienes segundos contados para dibujarlo. No pienses, solo traza.
          </p>
        </div>

        {phase === "idle" && (
          <div className="bg-white border-4 border-[#1a1a1a] rounded-3xl shadow-hard-md p-8 sm:p-10 max-w-2xl pop-in" data-testid="pen-setup">
            <div className="font-mono text-xs uppercase tracking-[0.25em] font-bold mb-3">elige duración</div>
            <div className="flex flex-wrap gap-3 mb-8">
              {DURATIONS.map((d) => (
                <button
                  key={d}
                  data-testid={`duration-${d}`}
                  onClick={() => setDuration(d)}
                  className={`press border-4 border-[#1a1a1a] rounded-2xl px-6 py-4 font-display font-black text-2xl shadow-hard-sm ${
                    duration === d ? "bg-[#06B6D4] text-white" : "bg-white"
                  }`}
                >
                  {d}s
                </button>
              ))}
            </div>
            <button
              data-testid="get-challenge-btn"
              onClick={() => fetchPrompt(duration)}
              disabled={loading}
              className="press w-full bg-[#1a1a1a] text-white border-4 border-[#1a1a1a] rounded-2xl px-6 py-5 font-display font-black text-2xl shadow-hard-md disabled:opacity-60"
            >
              {loading ? "Pensando..." : "obtener reto →"}
            </button>
          </div>
        )}

        {phase === "ready" && data && (
          <div ref={cardRef} className="bg-white border-4 border-[#1a1a1a] rounded-3xl shadow-hard-lg p-8 sm:p-12 pop-in relative" data-testid="pen-ready">
            <div className="absolute top-3 right-4 font-mono text-[10px] uppercase tracking-[0.25em] text-[#737373] font-bold">
              guango · pen battle
            </div>
            <div className="font-mono text-xs uppercase tracking-[0.25em] font-bold mb-4 text-[#737373]">tu reto</div>
            <h2 className="font-display font-black text-3xl sm:text-5xl leading-tight mb-5">
              {reto}
            </h2>
            {tip && (
              <div className="bg-[#06B6D4] border-4 border-[#1a1a1a] rounded-2xl px-5 py-3 inline-block font-mono text-sm font-bold mb-8 tilt-1">
                💡 {tip}
              </div>
            )}
            <div className="flex flex-wrap gap-3" data-exclude-from-image="true">
              <button
                data-testid="start-timer-btn"
                onClick={start}
                className="press inline-flex items-center gap-2 bg-[#22c55e] border-4 border-[#1a1a1a] rounded-xl px-6 py-3 font-display font-black text-xl shadow-hard-md"
              >
                <Play className="w-5 h-5" strokeWidth={3} /> empezar {duration}s
              </button>
              <button
                data-testid="new-prompt-btn"
                onClick={() => fetchPrompt(duration)}
                disabled={loading}
                className="press inline-flex items-center gap-2 bg-white border-4 border-[#1a1a1a] rounded-xl px-6 py-3 font-mono text-sm uppercase tracking-wider font-bold shadow-hard-sm"
              >
                <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} strokeWidth={3} /> otro reto
              </button>
              <ShareButton promptId={data.id} color={COLOR} testId="pen-battle-share" />
              <DownloadImageButton
                targetRef={cardRef}
                fileName={`guango-pen-battle-${data.id.slice(0, 8)}.png`}
                color={COLOR}
                testId="pen-battle-download"
              />
            </div>
          </div>
        )}

        {phase === "running" && (
          <div data-testid="pen-running">
            <div className="bg-[#1a1a1a] text-white border-4 border-[#1a1a1a] rounded-2xl px-5 py-3 inline-block font-mono text-xs uppercase tracking-[0.25em] font-bold mb-6">
              dibujando: {reto}
            </div>
            <CountdownTimer
              duration={duration}
              color={COLOR}
              running
              onComplete={finish}
            />
          </div>
        )}

        {phase === "done" && (
          <div className="bg-[#06B6D4] border-4 border-[#1a1a1a] rounded-3xl shadow-hard-lg p-8 sm:p-12 pop-in" data-testid="pen-done">
            <div className="font-display font-black text-5xl sm:text-7xl mb-5">¡tiempo!</div>
            <p className="font-mono text-base mb-8">
              Tu reto era: <span className="font-bold">{reto}</span>. Comparte tu dibujo donde quieras 📸
            </p>
            <div className="flex flex-wrap gap-3">
              <button
                data-testid="another-round-btn"
                onClick={() => fetchPrompt(duration)}
                disabled={loading}
                className="press inline-flex items-center gap-2 bg-[#1a1a1a] text-white border-4 border-[#1a1a1a] rounded-xl px-6 py-3 font-display font-black text-xl shadow-hard-md"
              >
                <RotateCcw className="w-5 h-5" strokeWidth={3} /> otra ronda
              </button>
              <button
                data-testid="change-duration-btn"
                onClick={reset}
                className="press inline-flex items-center gap-2 bg-white border-4 border-[#1a1a1a] rounded-xl px-6 py-3 font-mono text-sm uppercase tracking-wider font-bold shadow-hard-sm"
              >
                cambiar duración
              </button>
            </div>
          </div>
        )}
      </section>
    </Layout>
  );
};

export default PenBattle;
